import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import PremiumBadge from '../premium/PremiumBadge';
import PremiumLockedSection from '../premium/PremiumLockedSection';
import usePremiumEntitlements from '../../hooks/usePremiumEntitlements';

type FuelItem = {
  id: number;
  fill_date: string;
  odometer: number | null;
  gallons: number;
  total_cost_cents: number | null;
  fuel_type: string;
  custom_fuel_label: string | null;
  is_full_tank: boolean;
  mpg: number | null;
};

type Props = {
  vehicleId: number;
};

const fuelLabel = (item: FuelItem) =>
  item.fuel_type === 'custom' && item.custom_fuel_label
    ? item.custom_fuel_label
    : item.fuel_type.replace(/_/g, ' ');

const FuelTrackingPanel: React.FC<Props> = ({ vehicleId }) => {
  const { hasCapability, loading: entLoading } = usePremiumEntitlements();
  const entitled = hasCapability('premium_maintenance');
  const [items, setItems] = useState<FuelItem[]>([]);
  const [summary, setSummary] = useState<{
    recent_mpg?: number | null;
    cost_cents_month?: number;
    fill_count?: number;
  }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fillDate, setFillDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [odometer, setOdometer] = useState('');
  const [gallons, setGallons] = useState('');
  const [totalCost, setTotalCost] = useState('');
  const [fuelType, setFuelType] = useState('regular');
  const [customFuel, setCustomFuel] = useState('');
  const [isFullTank, setIsFullTank] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getFuelEntries(vehicleId);
      setItems((data.items as FuelItem[]) ?? []);
      setSummary(data.summary ?? {});
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load fuel history.');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const logFillUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entitled) return;
    setSaving(true);
    setError(null);
    try {
      const totalCents = totalCost ? Math.round(parseFloat(totalCost) * 100) : null;
      await apiService.postFuelEntry({
        action: 'create',
        vehicle_id: vehicleId,
        fill_date: fillDate,
        odometer: odometer ? Number(odometer) : null,
        gallons: parseFloat(gallons),
        total_cost_cents: totalCents,
        fuel_type: fuelType,
        custom_fuel_label: fuelType === 'custom' ? customFuel : null,
        is_full_tank: isFullTank,
      });
      setGallons('');
      setTotalCost('');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save fill-up.');
    } finally {
      setSaving(false);
    }
  };

  const form = (
    <form onSubmit={(e) => void logFillUp(e)} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-zinc-500">
          Date
          <input
            type="date"
            className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            value={fillDate}
            onChange={(e) => setFillDate(e.target.value)}
            required
          />
        </label>
        <label className="block text-xs text-zinc-500">
          Odometer
          <input
            type="number"
            inputMode="numeric"
            className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            value={odometer}
            onChange={(e) => setOdometer(e.target.value)}
            placeholder="84221"
          />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-zinc-500">
          Gallons
          <input
            type="number"
            step="0.001"
            className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            value={gallons}
            onChange={(e) => setGallons(e.target.value)}
            required
          />
        </label>
        <label className="block text-xs text-zinc-500">
          Total ($)
          <input
            type="number"
            step="0.01"
            className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            value={totalCost}
            onChange={(e) => setTotalCost(e.target.value)}
            placeholder="47.82"
          />
        </label>
      </div>
      <label className="block text-xs text-zinc-500">
        Fuel
        <select
          className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
          value={fuelType}
          onChange={(e) => setFuelType(e.target.value)}
        >
          <option value="regular">Regular</option>
          <option value="midgrade">Mid-grade</option>
          <option value="premium">Premium</option>
          <option value="e85">E85</option>
          <option value="diesel">Diesel</option>
          <option value="race">Race fuel</option>
          <option value="custom">Custom</option>
        </select>
      </label>
      {fuelType === 'custom' ? (
        <label className="block text-xs text-zinc-500">
          Custom label
          <input
            className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            value={customFuel}
            onChange={(e) => setCustomFuel(e.target.value)}
            placeholder="E60 blend"
          />
        </label>
      ) : null}
      <label className="flex items-center gap-2 text-xs text-zinc-400">
        <input
          type="checkbox"
          checked={isFullTank}
          onChange={(e) => setIsFullTank(e.target.checked)}
          className="checkbox-control"
        />
        Full tank (used for MPG)
      </label>
      <Button type="submit" size="sm" disabled={saving || !gallons}>
        {saving ? 'Saving…' : 'Log fill-up'}
      </Button>
    </form>
  );

  return (
    <section className="space-y-4" aria-labelledby="fuel-tracking-heading">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="fuel-tracking-heading" className="text-sm font-semibold text-white">
          Fuel tracking
        </h3>
        <PremiumBadge featureLabel="Fuel tracking" variant="compact" />
      </div>
      <p className="text-xs text-zinc-500">
        Track fill-ups, fuel economy, and ownership costs. Fuel history is private.
      </p>

      {error ? <p className="text-sm text-red-400">{error}</p> : null}

      {entitled && summary.recent_mpg != null ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-white/10 bg-black/20 p-3">
            <p className="text-[10px] uppercase tracking-wide text-zinc-500">Recent average</p>
            <p className="text-xl font-semibold text-white tabular-nums">{summary.recent_mpg} mpg</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-black/20 p-3">
            <p className="text-[10px] uppercase tracking-wide text-zinc-500">This month</p>
            <p className="text-xl font-semibold text-white tabular-nums">
              ${((summary.cost_cents_month ?? 0) / 100).toFixed(0)}
            </p>
          </div>
        </div>
      ) : null}

      <PremiumLockedSection
        entitled={entitled}
        capability="premium_maintenance"
        featureKey="fuel_tracking"
        title="Fuel tracking"
        description="Log fill-ups to understand fuel economy, fuel costs, and how the car changes over time."
        helperText="Premium helps track MPG between full tanks and monthly fuel spend."
        surface="maintenance_panel"
      >
        {form}
      </PremiumLockedSection>

      {loading || entLoading ? (
        <p className="text-xs text-zinc-500">Loading fuel history…</p>
      ) : entitled && items.length === 0 ? (
        <EmptyState
          variant="dense"
          title="Track your fuel"
          description="Log fill-ups to understand fuel economy and ownership costs over time."
        />
      ) : entitled && items.length > 0 ? (
        <ul className="divide-y divide-white/[0.06] rounded-xl border border-white/10 overflow-hidden">
          {items.slice(0, 12).map((item) => (
            <li key={item.id} className="px-4 py-3 bg-black/20 flex justify-between gap-3">
              <div>
                <p className="text-sm text-gray-100">{item.fill_date}</p>
                <p className="text-xs text-zinc-500 capitalize">{fuelLabel(item)}</p>
              </div>
              <div className="text-right text-xs text-zinc-400 tabular-nums">
                <p>{item.gallons.toFixed(1)} gal</p>
                {item.total_cost_cents != null ? (
                  <p>${(item.total_cost_cents / 100).toFixed(2)}</p>
                ) : null}
                {item.mpg != null ? <p className="text-orange-300/90">{item.mpg} mpg</p> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

export default FuelTrackingPanel;
